import { useState } from "react";

function useLocalStorage<T>(key: string) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") return "" as unknown as T;

    const item = window.localStorage.getItem(key);
    if (!item) return "" as unknown as T;

    try {
      return JSON.parse(item);
    } catch {
      // A previous write must have stored a non-JSON value (e.g.
      // localStorage.setItem(key, undefined) silently coerces to the
      // literal string "undefined", which JSON.parse then throws on).
      // That throw was happening inside this useState initializer, i.e.
      // during render/hydration -- crashing the whole app to
      // client-only rendering (React error #423) on every future page
      // load until storage was cleared manually. Drop the corrupted
      // entry and fall back to empty instead of ever throwing here.
      window.localStorage.removeItem(key);
      return "" as unknown as T;
    }
  });

  const setValue = (value: T) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      if (typeof window !== "undefined") {
        try {
          if (valueToStore === undefined) {
            // JSON.stringify(undefined) returns the value `undefined`,
            // and localStorage.setItem coerces a non-string value to
            // the literal string "undefined" -- exactly the corruption
            // this hook now has to defend against on read. Never write
            // it in the first place.
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
          }
        } catch {}
      }
    } catch (error) {}
  };

  return { storedValue, setValue };
}

export default useLocalStorage;
